import React, { useEffect, useState, useMemo, useRef } from 'react';
import { getSmsBroadcastAbonnes, sendSmsBroadcast, getSmsBroadcastHistory, getPartenaires, getWhatsappStatus, initWhatsapp, logoutWhatsapp } from '../services/api';

interface Abonne {
  id: number;
  prenomnom: string;
  prenom: string;
  nom: string;
  telephone: string;
  reference: string;
  secteur: string;
  localite: string;
  partenaire: string;
  partenaire_id: number;
}

interface Broadcast {
  id: number;
  message: string;
  total_recipients: number;
  success_count: number;
  fail_count: number;
  errors: any[];
  created_at: string;
  user_nom: string;
  user_prenom: string;
  channel?: 'sms' | 'whatsapp';
}

interface WaStatus {
  state: 'idle' | 'starting' | 'qr' | 'authenticated' | 'ready' | 'auth_failure' | 'disconnected';
  qr: string | null;
  connected: boolean;
  phone: string | null;
  pushname: string | null;
  error: string | null;
}

const PLACEHOLDERS = [
  { tag: '{prenom}', label: 'Prenom', desc: 'Prenom de l\'abonne' },
  { tag: '{nom}', label: 'Nom', desc: 'Nom de l\'abonne' },
  { tag: '{prenomnom}', label: 'Prenom Nom', desc: 'Nom complet' },
  { tag: '{reference}', label: 'Reference', desc: 'Reference abonne' },
  { tag: '{localite}', label: 'Localite', desc: 'Localite de l\'abonne' },
  { tag: '{secteur}', label: 'Secteur', desc: 'Secteur de l\'abonne' },
];

const SmsBroadcast: React.FC = () => {
  const [tab, setTab] = useState<'compose' | 'history'>('compose');

  // Filters
  const [partenaires, setPartenaires] = useState<any[]>([]);
  const [filterPartenaire, setFilterPartenaire] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // Abonnes
  const [abonnes, setAbonnes] = useState<Abonne[]>([]);
  const [loadingAbonnes, setLoadingAbonnes] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Ad-hoc recipients (numeros libres, non abonnes)
  const [customRecipients, setCustomRecipients] = useState<{ nom: string; telephone: string }[]>([]);
  const [customNom, setCustomNom] = useState('');
  const [customTel, setCustomTel] = useState('');

  // Message
  const [message, setMessage] = useState('');
  const [personalisation, setPersonalisation] = useState(true);

  // Channel (sms or whatsapp)
  const [channel, setChannel] = useState<'sms' | 'whatsapp'>('sms');
  const [waStatus, setWaStatus] = useState<WaStatus | null>(null);
  const [waLoading, setWaLoading] = useState(false);
  const [waPanelOpen, setWaPanelOpen] = useState(false);
  const waPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sending
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: number; fail: number; total: number } | null>(null);

  // History
  const [history, setHistory] = useState<Broadcast[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load partenaires
  useEffect(() => {
    getPartenaires().then(r => setPartenaires(r.data)).catch(() => {});
  }, []);

  // Fetch WhatsApp status (once + on poll during pairing)
  const fetchWaStatus = async () => {
    try {
      const r = await getWhatsappStatus();
      setWaStatus(r.data);
      return r.data as WaStatus;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    fetchWaStatus();
  }, []);

  // Poll while pairing (qr / starting / authenticated) — stop when ready or idle
  useEffect(() => {
    if (!waStatus) return;
    const shouldPoll = ['starting', 'qr', 'authenticated'].includes(waStatus.state);
    if (shouldPoll && !waPollRef.current) {
      waPollRef.current = setInterval(fetchWaStatus, 2000);
    }
    if (!shouldPoll && waPollRef.current) {
      clearInterval(waPollRef.current);
      waPollRef.current = null;
    }
    return () => {
      if (waPollRef.current) {
        clearInterval(waPollRef.current);
        waPollRef.current = null;
      }
    };
  }, [waStatus?.state]);

  const handleWaInit = async () => {
    setWaLoading(true);
    try {
      const r = await initWhatsapp();
      setWaStatus(r.data);
      setWaPanelOpen(true);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erreur initialisation WhatsApp');
    } finally {
      setWaLoading(false);
    }
  };

  const handleWaLogout = async () => {
    if (!window.confirm('Deconnecter la session WhatsApp ? Vous devrez re-scanner le QR code pour envoyer.')) return;
    setWaLoading(true);
    try {
      const r = await logoutWhatsapp();
      setWaStatus(r.data);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erreur deconnexion');
    } finally {
      setWaLoading(false);
    }
  };

  // Load abonnes with filters
  useEffect(() => {
    setLoadingAbonnes(true);
    const params: Record<string, string> = {};
    if (filterPartenaire) params.partenaire_id = filterPartenaire;
    if (filterSearch) params.search = filterSearch;
    getSmsBroadcastAbonnes(params)
      .then(r => {
        setAbonnes(r.data.data || r.data);
        setSelectedIds(new Set());
        setSelectAll(false);
      })
      .catch(() => setAbonnes([]))
      .finally(() => setLoadingAbonnes(false));
  }, [filterPartenaire, filterSearch]);

  // Load history
  useEffect(() => {
    if (tab === 'history') {
      setLoadingHistory(true);
      getSmsBroadcastHistory()
        .then(r => setHistory(r.data.data || r.data))
        .catch(() => setHistory([]))
        .finally(() => setLoadingHistory(false));
    }
  }, [tab]);

  // Select all handler
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(abonnes.map(a => a.id)));
    }
    setSelectAll(!selectAll);
  };

  // Toggle single selection
  const toggleSelect = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
    setSelectAll(next.size === abonnes.length);
  };

  // Message character count
  const charCount = message.length;
  const smsCount = Math.ceil(charCount / 160) || 1;

  // Preview with first selected abonne
  const previewAbonne = useMemo(() => {
    if (!personalisation || selectedIds.size === 0) return null;
    const first = abonnes.find(a => selectedIds.has(a.id));
    return first || null;
  }, [selectedIds, abonnes, personalisation]);

  const previewMessage = useMemo(() => {
    if (!previewAbonne || !personalisation) return message;
    return message
      .replace(/\{prenom\}/g, previewAbonne.prenom || '')
      .replace(/\{nom\}/g, previewAbonne.nom || '')
      .replace(/\{prenomnom\}/g, previewAbonne.prenomnom || '')
      .replace(/\{reference\}/g, previewAbonne.reference || '')
      .replace(/\{localite\}/g, previewAbonne.localite || '')
      .replace(/\{secteur\}/g, previewAbonne.secteur || '');
  }, [message, previewAbonne, personalisation]);

  // Insert placeholder at cursor
  const insertPlaceholder = (tag: string) => {
    const textarea = document.getElementById('sms-message') as HTMLTextAreaElement;
    if (!textarea) { setMessage(prev => prev + tag); return; }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMsg = message.substring(0, start) + tag + message.substring(end);
    setMessage(newMsg);
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + tag.length;
    }, 0);
  };

  // Add custom recipient (validation basique senegal)
  const normalizePhone = (raw: string) => {
    const cleaned = raw.replace(/\D/g, '');
    if (cleaned.length < 6) return null;
    return cleaned;
  };

  const handleAddCustom = () => {
    const tel = normalizePhone(customTel);
    if (!tel) { alert('Numero invalide'); return; }
    // Dedup by phone (customs + abonnes)
    const already = customRecipients.some(c => normalizePhone(c.telephone) === tel)
      || abonnes.some(a => selectedIds.has(a.id) && normalizePhone(a.telephone) === tel);
    if (already) { alert('Ce numero est deja dans la liste'); return; }
    setCustomRecipients(prev => [...prev, { nom: customNom.trim() || '(sans nom)', telephone: tel }]);
    setCustomNom('');
    setCustomTel('');
  };

  const handleRemoveCustom = (idx: number) => {
    setCustomRecipients(prev => prev.filter((_, i) => i !== idx));
  };

  const totalRecipients = selectedIds.size + customRecipients.length;

  // Send
  const handleSend = async () => {
    if (totalRecipients === 0) return alert('Selectionnez au moins un destinataire');
    if (!message.trim()) return alert('Saisissez un message');
    if (channel === 'whatsapp' && !waStatus?.connected) {
      return alert('WhatsApp non connecte. Cliquez sur "Connecter WhatsApp" et scannez le QR code.');
    }
    const confirmTxt = channel === 'whatsapp'
      ? `Envoyer ce message WhatsApp a ${totalRecipients} destinataire(s) ?\n\nAstuce : envois espaces de ~1.2s pour eviter le blocage.\n\nConfirmer ?`
      : `Envoyer ce SMS a ${totalRecipients} destinataire(s) ?\n\nCout estime : ${totalRecipients} SMS x ${smsCount} partie(s) = ${totalRecipients * smsCount} SMS\n\nConfirmer ?`;
    if (!window.confirm(confirmTxt)) return;

    setSending(true);
    setSendResult(null);
    try {
      const res = await sendSmsBroadcast({
        message,
        abonne_ids: Array.from(selectedIds),
        personalisation,
        channel,
        custom_recipients: customRecipients,
      });
      setSendResult({
        success: res.data.success_count || res.data.success || 0,
        fail: res.data.fail_count || res.data.fail || 0,
        total: res.data.total || 0,
      });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const fmt = (d: string) => {
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: '#1565C0' }}>Diffusion SMS / WhatsApp</h2>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: 13 }}>Envoi groupe de messages personnalises aux abonnes</p>
        </div>
        {/* Channel toggle */}
        <div style={{ display: 'flex', gap: 0, border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden' }}>
          <button
            onClick={() => setChannel('sms')}
            style={{
              padding: '8px 18px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
              background: channel === 'sms' ? '#1565C0' : '#fff',
              color: channel === 'sms' ? '#fff' : '#666',
              transition: 'all 0.2s',
            }}
          >SMS</button>
          <button
            onClick={() => setChannel('whatsapp')}
            style={{
              padding: '8px 18px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
              background: channel === 'whatsapp' ? '#25D366' : '#fff',
              color: channel === 'whatsapp' ? '#fff' : '#666',
              display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.606 3.5 4.52 4.6.717.4 1.87 1.288 2.63 1.288.4 0 1.55-.32 1.842-.734.174-.243.244-.526.244-.847v-.516c-.014-.244-1.246-.716-1.475-.716zm-2.63 5.106c-1.892 0-3.723-.53-5.322-1.518l-3.6 1.16 1.18-3.514c-1.104-1.66-1.706-3.618-1.706-5.635 0-5.593 4.548-10.14 10.14-10.14 5.607 0 10.156 4.547 10.156 10.14 0 5.607-4.55 10.156-10.14 10.156zm-.014-22.31C10.19-.001 5.06 5.13 5.06 11.464c0 2.164.6 4.283 1.746 6.132L4.573 24l6.505-2.102c1.778.976 3.788 1.49 5.836 1.49 6.334 0 11.464-5.128 11.464-11.462S22.804.014 16.47.014z"/></svg>
            WhatsApp
          </button>
        </div>
      </div>

      {/* WhatsApp connection panel */}
      {channel === 'whatsapp' && (
        <WhatsappPanel
          status={waStatus}
          loading={waLoading}
          panelOpen={waPanelOpen}
          togglePanel={() => setWaPanelOpen(o => !o)}
          onInit={handleWaInit}
          onLogout={handleWaLogout}
          onRefresh={fetchWaStatus}
        />
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20 }}>
        <button
          onClick={() => setTab('compose')}
          style={{
            padding: '10px 28px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
            background: tab === 'compose' ? '#1565C0' : '#e8e8e8',
            color: tab === 'compose' ? '#fff' : '#666',
            borderRadius: '8px 0 0 8px',
          }}
        >
          Composer un SMS
        </button>
        <button
          onClick={() => setTab('history')}
          style={{
            padding: '10px 28px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
            background: tab === 'history' ? '#1565C0' : '#e8e8e8',
            color: tab === 'history' ? '#fff' : '#666',
            borderRadius: '0 8px 8px 0',
          }}
        >
          Historique
        </button>
      </div>

      {tab === 'compose' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
          {/* LEFT: Message composer */}
          <div>
            {/* Message card */}
            <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 16 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#333' }}>Message</h3>

              {/* Personalisation toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={personalisation}
                    onChange={e => setPersonalisation(e.target.checked)}
                    style={{ width: 18, height: 18, accentColor: '#1565C0' }}
                  />
                  <span style={{ fontWeight: 600, color: '#333' }}>Personnalisation</span>
                </label>
                <span style={{ fontSize: 12, color: '#999' }}>Inserer des variables pour chaque abonne</span>
              </div>

              {/* Placeholder buttons */}
              {personalisation && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                  {PLACEHOLDERS.map(p => (
                    <button
                      key={p.tag}
                      onClick={() => insertPlaceholder(p.tag)}
                      title={p.desc}
                      style={{
                        padding: '4px 10px', borderRadius: 14, border: '1px solid #1565C0',
                        background: '#e8f0fe', color: '#1565C0', fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', transition: 'all 0.2s',
                      }}
                    >
                      {p.tag}
                    </button>
                  ))}
                </div>
              )}

              {/* Textarea */}
              <textarea
                id="sms-message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Saisissez votre message ici...&#10;&#10;Exemple : Bonjour {prenom}, votre facture est disponible. Ref: {reference}"
                rows={6}
                style={{
                  width: '100%', padding: 12, border: '1.5px solid #ddd', borderRadius: 8,
                  fontSize: 14, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.6,
                }}
              />

              {/* Char count */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: '#888' }}>
                <span>{charCount} caractere(s) - {smsCount} SMS par destinataire</span>
                <span style={{ color: charCount > 160 ? '#f44336' : '#4CAF50' }}>
                  {charCount <= 160 ? '1 partie' : `${smsCount} parties`}
                </span>
              </div>
            </div>

            {/* Preview card */}
            {message && (
              <div style={{ background: '#f8f9ff', borderRadius: 10, padding: 16, border: '1px dashed #1565C0', marginBottom: 16 }}>
                <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#1565C0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Apercu {personalisation && previewAbonne ? `(${previewAbonne.prenomnom})` : ''}
                </h4>
                <div style={{
                  background: '#1565C0', color: '#fff', borderRadius: 12, padding: '12px 16px',
                  fontSize: 14, lineHeight: 1.6, maxWidth: 340, position: 'relative',
                  borderBottomLeftRadius: 4,
                }}>
                  {previewMessage || message}
                </div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>FORGEST</div>
              </div>
            )}

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={sending || totalRecipients === 0 || !message.trim() || (channel === 'whatsapp' && !waStatus?.connected)}
              style={{
                width: '100%', padding: '14px 24px', border: 'none', borderRadius: 8,
                background: sending
                  ? (channel === 'whatsapp' ? '#81C784' : '#90CAF9')
                  : (totalRecipients === 0 || !message.trim() || (channel === 'whatsapp' && !waStatus?.connected))
                    ? '#ccc'
                    : (channel === 'whatsapp' ? '#25D366' : '#1565C0'),
                color: '#fff', fontSize: 15, fontWeight: 700, cursor: sending ? 'wait' : 'pointer',
                transition: 'all 0.3s',
              }}
            >
              {sending
                ? 'Envoi en cours...'
                : `Envoyer ${channel === 'whatsapp' ? 'sur WhatsApp' : 'un SMS'} a ${totalRecipients} destinataire(s)`
              }
            </button>

            {/* Send result */}
            {sendResult && (
              <div style={{
                marginTop: 12, padding: 14, borderRadius: 8,
                background: sendResult.fail === 0 ? '#E8F5E9' : '#FFF3E0',
                border: `1px solid ${sendResult.fail === 0 ? '#4CAF50' : '#FF9800'}`,
              }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: sendResult.fail === 0 ? '#2E7D32' : '#E65100', marginBottom: 6 }}>
                  Envoi termine !
                </div>
                <div style={{ fontSize: 13, display: 'flex', gap: 20 }}>
                  <span>Total : <strong>{sendResult.total}</strong></span>
                  <span style={{ color: '#2E7D32' }}>Reussis : <strong>{sendResult.success}</strong></span>
                  {sendResult.fail > 0 && <span style={{ color: '#f44336' }}>Echecs : <strong>{sendResult.fail}</strong></span>}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Recipient selection */}
          <div>
            <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#333' }}>
                Destinataires
                <span style={{ fontSize: 13, fontWeight: 400, color: '#888', marginLeft: 10 }}>
                  {totalRecipients} total
                  {customRecipients.length > 0 && (
                    <span style={{ color: '#E8943A', fontWeight: 600 }}>
                      {' '}({customRecipients.length} manuel{customRecipients.length > 1 ? 's' : ''})
                    </span>
                  )}
                </span>
              </h3>

              {/* Ad-hoc phone entry */}
              <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 8, padding: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#E65100', marginBottom: 8 }}>
                  Ajouter un numero libre (hors abonnes)
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    placeholder="Nom (optionnel)"
                    value={customNom}
                    onChange={e => setCustomNom(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddCustom(); }}
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, minWidth: 0 }}
                  />
                  <input
                    type="tel"
                    placeholder="77 123 45 67"
                    value={customTel}
                    onChange={e => setCustomTel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddCustom(); }}
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, minWidth: 0 }}
                  />
                  <button
                    onClick={handleAddCustom}
                    disabled={!customTel.trim()}
                    style={{
                      padding: '7px 14px', border: 'none', borderRadius: 6, cursor: customTel.trim() ? 'pointer' : 'not-allowed',
                      background: customTel.trim() ? '#E8943A' : '#ccc', color: '#fff', fontWeight: 700, fontSize: 12,
                    }}
                  >+ Ajouter</button>
                </div>

                {customRecipients.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {customRecipients.map((c, idx) => (
                      <span
                        key={idx}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: '#fff', border: '1px solid #FFB74D', color: '#E65100',
                          padding: '3px 8px 3px 10px', borderRadius: 14, fontSize: 12, fontWeight: 600,
                        }}
                        title={`${c.nom} - ${c.telephone}`}
                      >
                        <span style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.nom !== '(sans nom)' ? `${c.nom} · ` : ''}{c.telephone}
                        </span>
                        <button
                          onClick={() => handleRemoveCustom(idx)}
                          style={{ background: 'transparent', border: 'none', color: '#E65100', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}
                          title="Retirer"
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Filters */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <select
                  value={filterPartenaire}
                  onChange={e => setFilterPartenaire(e.target.value)}
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                >
                  <option value="">Tous les partenaires</option>
                  {partenaires.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.libelle}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={filterSearch}
                  onChange={e => setFilterSearch(e.target.value)}
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                />
              </div>

              {/* Select all */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '2px solid #eee', marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    style={{ width: 16, height: 16, accentColor: '#1565C0' }}
                  />
                  Tout selectionner ({abonnes.length})
                </label>
                {selectedIds.size > 0 && (
                  <button
                    onClick={() => { setSelectedIds(new Set()); setSelectAll(false); }}
                    style={{ background: 'none', border: 'none', color: '#f44336', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  >
                    Deselectionner tout
                  </button>
                )}
              </div>

              {/* Abonnes list */}
              <div style={{ maxHeight: 450, overflowY: 'auto' }}>
                {loadingAbonnes ? (
                  <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>Chargement...</div>
                ) : abonnes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>Aucun abonne avec telephone</div>
                ) : (
                  abonnes.map(a => (
                    <div
                      key={a.id}
                      onClick={() => toggleSelect(a.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px',
                        borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                        background: selectedIds.has(a.id) ? '#e8f0fe' : 'transparent',
                        borderRadius: 4, transition: 'background 0.15s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(a.id)}
                        onChange={() => toggleSelect(a.id)}
                        onClick={e => e.stopPropagation()}
                        style={{ width: 16, height: 16, accentColor: '#1565C0', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {a.prenomnom || `${a.prenom} ${a.nom}`}
                        </div>
                        <div style={{ fontSize: 11, color: '#888', display: 'flex', gap: 8 }}>
                          <span>{a.telephone}</span>
                          {a.localite && <span>| {a.localite}</span>}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: '#aaa', textAlign: 'right', flexShrink: 0 }}>
                        {a.reference}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* HISTORY TAB */
        <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {loadingHistory ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Chargement...</div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun envoi pour le moment</div>
          ) : (
            <table className="data-table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Canal</th>
                  <th>Expediteur</th>
                  <th>Message</th>
                  <th>Destinataires</th>
                  <th>Reussis</th>
                  <th>Echecs</th>
                  <th>Taux</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => {
                  const rate = h.total_recipients > 0 ? Math.round((h.success_count / h.total_recipients) * 100) : 0;
                  const isWa = h.channel === 'whatsapp';
                  return (
                    <tr key={h.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmt(h.created_at)}</td>
                      <td>
                        <span style={{
                          padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                          background: isWa ? '#E8F5E9' : '#E3F2FD',
                          color: isWa ? '#1B5E20' : '#0D47A1',
                        }}>{isWa ? 'WhatsApp' : 'SMS'}</span>
                      </td>
                      <td>{h.user_prenom} {h.user_nom}</td>
                      <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={h.message}>
                        {h.message}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700 }}>{h.total_recipients}</td>
                      <td style={{ textAlign: 'center', color: '#2E7D32', fontWeight: 700 }}>{h.success_count}</td>
                      <td style={{ textAlign: 'center', color: h.fail_count > 0 ? '#f44336' : '#999', fontWeight: 700 }}>{h.fail_count}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                          background: rate >= 90 ? '#E8F5E9' : rate >= 50 ? '#FFF3E0' : '#FFEBEE',
                          color: rate >= 90 ? '#2E7D32' : rate >= 50 ? '#E65100' : '#C62828',
                        }}>
                          {rate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// WhatsApp status panel — shows connection state, QR to scan, logout button.
// -----------------------------------------------------------------------------
const WhatsappPanel: React.FC<{
  status: WaStatus | null;
  loading: boolean;
  panelOpen: boolean;
  togglePanel: () => void;
  onInit: () => void;
  onLogout: () => void;
  onRefresh: () => void;
}> = ({ status, loading, panelOpen, togglePanel, onInit, onLogout, onRefresh }) => {
  const state = status?.state || 'idle';
  const connected = status?.connected || false;
  const showQr = state === 'qr' && status?.qr;
  const showStarting = state === 'starting' || state === 'authenticated';

  const dotColor =
    connected ? '#25D366'
    : state === 'qr' ? '#FFA726'
    : state === 'starting' || state === 'authenticated' ? '#42A5F5'
    : state === 'auth_failure' ? '#EF5350'
    : '#B0BEC5';

  const stateLabel =
    connected ? `Connecte${status?.pushname ? ' (' + status.pushname + ')' : ''}`
    : state === 'qr' ? 'En attente du scan QR'
    : state === 'starting' ? 'Demarrage...'
    : state === 'authenticated' ? 'Authentifie, chargement de WhatsApp Web...'
    : state === 'auth_failure' ? `Echec d'authentification`
    : state === 'disconnected' ? 'Deconnecte'
    : 'Non initialise';

  return (
    <div style={{
      background: '#fff', borderRadius: 10, padding: 16, marginBottom: 16,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${connected ? '#25D366' : '#e0e0e0'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 240 }}>
          <span style={{ width: 10, height: 10, borderRadius: 5, background: dotColor, boxShadow: `0 0 6px ${dotColor}` }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#333' }}>WhatsApp {stateLabel}</div>
            {status?.phone && <div style={{ fontSize: 12, color: '#888' }}>+{status.phone}</div>}
            {status?.error && !connected && <div style={{ fontSize: 12, color: '#EF5350', marginTop: 2 }}>Erreur : {status.error}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!connected && !showStarting && (
            <button
              onClick={onInit}
              disabled={loading}
              style={{
                padding: '8px 16px', border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer',
                background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 13,
              }}
            >{loading ? '...' : (state === 'qr' ? 'Voir le QR' : 'Connecter WhatsApp')}</button>
          )}
          {(showStarting || state === 'qr') && (
            <button
              onClick={onRefresh}
              style={{
                padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer',
                background: '#fff', color: '#555', fontWeight: 600, fontSize: 12,
              }}
            >Rafraichir</button>
          )}
          {connected && (
            <button
              onClick={onLogout}
              disabled={loading}
              style={{
                padding: '8px 14px', border: '1px solid #EF5350', borderRadius: 6, cursor: loading ? 'wait' : 'pointer',
                background: '#fff', color: '#EF5350', fontWeight: 700, fontSize: 12,
              }}
            >Deconnecter</button>
          )}
          {(state === 'qr' || showStarting) && (
            <button
              onClick={togglePanel}
              style={{
                padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer',
                background: '#fff', color: '#555', fontWeight: 600, fontSize: 12,
              }}
            >{panelOpen ? 'Masquer' : 'Afficher'}</button>
          )}
        </div>
      </div>

      {/* QR code panel */}
      {(state === 'qr' || showStarting) && panelOpen && (
        <div style={{
          marginTop: 16, padding: 16, background: '#fafafa', borderRadius: 8, textAlign: 'center',
        }}>
          {showQr ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 12 }}>
                Ouvrez WhatsApp sur votre telephone {'>'} Menu {'>'} Appareils connectes {'>'} Connecter un appareil
              </div>
              <img src={status!.qr!} alt="QR WhatsApp" style={{ width: 260, height: 260, borderRadius: 8, background: '#fff', padding: 8, border: '1px solid #ddd' }} />
              <div style={{ fontSize: 11, color: '#888', marginTop: 10 }}>Le QR se renouvelle automatiquement. La page se met a jour toutes les 2s.</div>
            </>
          ) : (
            <div style={{ padding: 40, color: '#666', fontSize: 13 }}>
              Preparation de WhatsApp Web...
              <div style={{ marginTop: 8, fontSize: 11, color: '#999' }}>Cela peut prendre 15-30 secondes.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SmsBroadcast;
